import { useState, useEffect, useCallback, useRef } from 'react';
import Box        from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import {
  sortItems, groupBySubcategory,
  PrefOverlay, GhostCard, ScrollableTabStrip,
  ShortlistChipStrip, ShortlistSectionHeader, ShortlistEmptyState,
  ShortlistHintStrip, AddPhotoDialog, ItemDetailModal, PageHeader,
  useRankSync, moveInList, RemoveConfirmDialog, plural,
} from './ShortlistShared3';
import { SourcePhotoLink, SourcePhotoPeek, useSourcePeek, useGlow } from './SourcePhoto';
import { formatFurniturePrice } from '../../../shared/formatPrice';
import { apiFetch, friendlyMessage } from '../../../api/apiClient';
import { getTokenSubject } from '../../../auth/token';
import { LoadingState, ErrorState, useToast } from '../../../shared/components/status';

import { GREEN, CHARCOAL, BORDER, MUTED, MUTED_FAINT, labelMono, bodyFont, appGridBg, PAGE_PX }
  from '../../../theme/tokens2';

// ─── Constants ────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'furniture',  label: 'Furniture'         },
  { id: 'bathroom',   label: 'Bathroom fittings' },
  { id: 'appliances', label: 'Appliances'        },
];

const DEFAULT_SUBCATEGORIES = {
  furniture:  ['Bed', 'Chair', 'Dining Table', 'Sofa'],
  bathroom:   ['Toilet Bowl', 'Sink', 'Shower', 'Bathtub', 'Tap / Faucet'],
  appliances: ['Oven', 'Dishwasher', 'Fridge', 'Washer / Dryer', 'Hood & Hob'],
};

const FIELDS = [
  { label: 'NAME',   field: 'namelabel', placeholder: 'Item name',              type: 'text'   },
  { label: 'PRICE',  field: 'price',     placeholder: '0.00',                   type: 'number', prefix: 'SGD', step: '0.01' },
  { label: 'VENDOR', field: 'vendor',    placeholder: 'Shop / brand',           type: 'text'   },
  { label: 'LINK',   field: 'link',      placeholder: 'https://…',              type: 'url'    },
  { label: 'Notes',  field: 'notes',     placeholder: 'Notes about this item…', textarea: true },
];

// ─── ItemCard ─────────────────────────────────────────────────────────────────

function ItemCard({ item, idx, arrLen, category, onMoveUp, onMoveDown, onRemove, onUpdated, scenesData, onOpenSource, isGlowing }) {
  const [modalOpen,   setModalOpen]   = useState(false);
  const [cardHovered, setCardHovered] = useState(false);
  const [cardRef,     glowSx]         = useGlow(isGlowing);

  const rank = String(idx + 1).padStart(2, '0');
  const name = item.namelabel || 'Untitled';

  return (
    <>
      <Box
        ref={cardRef}
        onMouseEnter={() => setCardHovered(true)}
        onMouseLeave={() => setCardHovered(false)}
        sx={{
          border: item.savvyproposal ? `1.5px solid ${GREEN}` : `0.5px solid ${BORDER}`,
          borderRadius: '3px',
          overflow: 'hidden',
          background: '#fff',
          position: 'relative',
          display: 'flex', flexDirection: 'column',
          scrollMarginTop: '12px', scrollMarginBottom: '12px',
          ...glowSx,
        }}
      >
        {/* ── Image area ── */}
        <Box sx={{ position: 'relative', width: '100%', aspectRatio: '4/5', overflow: 'hidden' }}>
          <Box
            component="button"
            type="button"
            onClick={() => setModalOpen(true)}
            aria-label={`Open details for ${name}`}
            sx={{ display: 'block', width: '100%', height: '100%', p: 0, border: 'none', background: 'none', cursor: 'pointer' }}
          >
            <Box
              component="img"
              src={item.imagesrc}
              alt={item.namelabel || category}
              loading="lazy"
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </Box>

          <Box sx={{
            position: 'absolute', bottom: 6, left: 6,
            ...labelMono, fontSize: '8px',
            background: 'rgba(18,18,18,0.62)', color: 'rgba(255,255,255,0.85)',
            px: '5px', py: '2px', pointerEvents: 'none',
          }}>
            {rank}
          </Box>

          {item.savvyproposal && (
            <Box sx={{
              position: 'absolute', bottom: 6, right: 6,
              ...labelMono, fontSize: '7px',
              background: 'rgba(255,255,255,0.92)', color: GREEN,
              border: `1px solid ${GREEN}`, px: '4px', py: '2px', pointerEvents: 'none',
            }}>
              Savvy pick
            </Box>
          )}

          <PrefOverlay
            onMoveUp={onMoveUp} onMoveDown={onMoveDown} onRemove={onRemove}
            isFirst={idx === 0} isLast={idx === arrLen - 1} cardHovered={cardHovered}
            itemLabel={name}
          />
        </Box>

        {/* ── Details ── */}
        <Box sx={{ px: '8px', pt: '7px', pb: '8px', borderTop: `0.5px solid ${BORDER}` }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '4px' }}>
            <Typography sx={{
              fontSize: '12px', fontWeight: 600, color: CHARCOAL,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              flex: 1, ...bodyFont,
            }}>
              {name}
            </Typography>
            <Box
              component="button"
              type="button"
              aria-label={`Edit details for ${name}`}
              onClick={() => setModalOpen(true)}
              sx={{
                background: 'none', border: 'none', cursor: 'pointer',
                p: 0, flexShrink: 0, color: MUTED_FAINT, display: 'flex',
                '&:hover': { color: GREEN }, transition: 'color 0.15s',
              }}
            >
              <EditOutlinedIcon sx={{ fontSize: '0.75rem' }} />
            </Box>
          </Box>

          {/* Price first, then vendor. A long vendor name is cut with "…". */}
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '4px', mt: '2px', minWidth: 0, overflow: 'hidden' }}>
            <Typography sx={{
              fontSize: '11px', fontWeight: 600, color: CHARCOAL, ...bodyFont,
              flexShrink: 0, maxWidth: '100%',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {formatFurniturePrice(item.price)}
            </Typography>
            {item.vendor && (
              <>
                <Typography sx={{ fontSize: '9px', color: MUTED_FAINT, flexShrink: 0 }}>·</Typography>
                <Typography sx={{
                  fontSize: '10px', color: MUTED, fontStyle: 'italic', minWidth: 0,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', ...bodyFont,
                }}>
                  {item.vendor}
                </Typography>
              </>
            )}
          </Box>

          {item.link ? (
            <Box
              component="a" href={item.link} target="_blank" rel="noopener noreferrer"
              sx={{
                display: 'block', mt: '3px',
                ...labelMono, fontSize: '8px', color: GREEN,
                textDecoration: 'none', '&:hover': { textDecoration: 'underline' },
              }}
            >
              Visit link ↗
            </Box>
          ) : (
            <Typography sx={{ ...labelMono, fontSize: '8px', color: MUTED_FAINT, mt: '3px' }}>
              No link
            </Typography>
          )}

          {/* Opens the Moodboard photo this came from, on top of this page */}
          <SourcePhotoLink
            item={item} scenesData={scenesData} showRoom
            onOpen={(it, scene) => onOpenSource(it, scene, `${it.subcategory} #${rank}`)}
          />
        </Box>
      </Box>

      <ItemDetailModal
        item={item} open={modalOpen}
        onClose={() => setModalOpen(false)} onUpdated={onUpdated}
        apiEndpoint={(id) => `/api/furnitureshortlist/${id}`}
        fields={FIELDS}
      />
    </>
  );
}

// ─── TabContent ───────────────────────────────────────────────────────────────

// refreshKey: goes up by one after the "See in photo" pop-up closes with
// changes, so this tab reloads its items quietly.
function TabContent({ tab, phoneNumber, scenesData, onOpenSource, glowItemId, refreshKey }) {
  const tabId = tab.id;
  const toast = useToast();

  const [items,            setItems]            = useState([]);
  const [status,           setStatus]           = useState('loading'); // 'loading' | 'ready' | 'error'
  const [loadError,        setLoadError]        = useState('');
  const [allSubcategories, setAllSubcategories] = useState(DEFAULT_SUBCATEGORIES[tabId]);
  const [activeChip,       setActiveChip]       = useState('all');
  const [addPhotoFor,      setAddPhotoFor]      = useState(null);
  const [removing,         setRemoving]         = useState(null);  // category in the confirm box
  const [removeOpen,       setRemoveOpen]       = useState(false); // (kept apart so the box doesn't go blank while closing)
  const [removeBusy,       setRemoveBusy]       = useState(false);
  const [removeError,      setRemoveError]      = useState('');

  // Saves the new order only after the customer moves a card.
  const queueRankSave = useRankSync('/api/furnitureshortlist/bulk');

  // Only saved items for this tab (the server filters to in_shortlist = true)
  const fetchItems = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setStatus('loading');
    try {
      const data = (await apiFetch(`/api/furnitureshortlist/my?tab=${encodeURIComponent(tabId)}`)) || [];
      setItems(sortItems(data));
      const fromData = [...new Set(data.map((i) => i.subcategory).filter(Boolean))];
      setAllSubcategories((prev) => [...new Set([...prev, ...fromData])]);
      setStatus('ready');
    } catch (err) {
      if (quiet) {
        toast.error("We couldn't refresh this list. Please reload the page.");
      } else {
        setLoadError(friendlyMessage(err));
        setStatus('error');
      }
    }
  }, [tabId, toast]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Skip the key this tab opened with: the load above already covers it.
  const openedWithKey = useRef(refreshKey);
  useEffect(() => {
    if (refreshKey !== openedWithKey.current) fetchItems({ quiet: true });
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps -- only when the key changes

  const grouped = groupBySubcategory(items);

  const updateSubcategory = (subcategory, updatedList) => {
    setItems((prev) => {
      const rest = prev.filter((i) => (i.subcategory || 'Uncategorised') !== subcategory);
      return sortItems([...rest, ...updatedList]);
    });
  };

  const move = (subcategory, idx, direction) => {
    const next = moveInList(grouped[subcategory] || [], idx, direction);
    if (!next) return;
    updateSubcategory(subcategory, next);
    queueRankSave(next);
  };

  const removeItem = async (subcategory, item) => {
    try {
      await apiFetch(`/api/furnitureshortlist/${item.id}`, { method: 'DELETE' });
      const next = (grouped[subcategory] || [])
        .filter((i) => i.id !== item.id)
        .map((it, i) => ({ ...it, ranking: i + 1 }));
      updateSubcategory(subcategory, next);
      if (next.length) queueRankSave(next);
      toast.success(`${item.namelabel || 'Item'} removed`);
    } catch (err) {
      toast.error("We couldn't remove this item. Please try again.");
    }
  };

  const handleItemUpdated = (updated) => {
    setItems((prev) => prev.map((i) => (i.id === updated.id ? { ...i, ...updated } : i)));
  };

  // ── Removing a category (the × on its chip) ───────────────────────────────
  // A category only exists through its items, so:
  //   empty     → the chip goes straight away, with Undo (nothing to save);
  //   not empty → confirm box; the server takes its items off the shortlist.
  // Afterwards the "All" chip is selected.
  const requestRemoveCategory = (category) => {
    const inIt = grouped[category] || [];
    if (inIt.length > 0) {
      // Counts are taken now, so the box doesn't change while it closes.
      setRemoveError('');
      setRemoving({
        name:  category,
        count: inIt.length,
        found: inIt.filter((i) => i.source_scene_id || i.region_id).length,
      });
      setRemoveOpen(true);
      return;
    }
    const index = allSubcategories.indexOf(category);
    setAllSubcategories((prev) => prev.filter((c) => c !== category));
    setActiveChip('all');
    toast.show({
      message: `${category} removed`,
      severity: 'success',
      actionLabel: 'Undo',
      duration: 6000,
      onAction: () => {
        setAllSubcategories((prev) => {
          if (prev.includes(category)) return prev;
          const next = [...prev];
          next.splice(Math.min(index, next.length), 0, category);
          return next;
        });
        setActiveChip(category);
      },
    });
  };

  const confirmRemoveCategory = async () => {
    const category = removing.name;
    setRemoveBusy(true);
    setRemoveError('');
    try {
      const query = `tab=${encodeURIComponent(tabId)}&subcategory=${encodeURIComponent(category)}`;
      await apiFetch(`/api/furnitureshortlist/category?${query}`, { method: 'DELETE' });
      setItems((prev) => prev.filter((i) => (i.subcategory || 'Uncategorised') !== category));
      setAllSubcategories((prev) => prev.filter((c) => c !== category));
      setActiveChip('all');
      setRemoveOpen(false);
      toast.success(`${category} removed`);
    } catch (err) {
      setRemoveError(friendlyMessage(err, `We couldn't remove ${category}. Please try again.`));
    } finally {
      setRemoveBusy(false);
    }
  };

  // Wording for the confirm box
  const rm = removing || { name: '', count: 0, found: 0 };

  // Your own photo — the server saves it straight into the shortlist
  const handlePhotoUploaded = async (url) => {
    if (!url) return; // the person removed the file inside the dialog
    try {
      await apiFetch('/api/furnitureshortlist', {
        method: 'POST',
        body: { tab: tabId, subcategory: addPhotoFor, imagesrc: url },
      });
      setAddPhotoFor(null);
      fetchItems({ quiet: true });
    } catch (err) {
      toast.error("Your photo uploaded, but we couldn't add it to your shortlist. Please try again.");
    }
  };

  if (status === 'loading') {
    return <LoadingState label={`Loading your ${tab.label.toLowerCase()}…`} />;
  }

  if (status === 'error') {
    return (
      <ErrorState
        title={`We couldn't load your ${tab.label.toLowerCase()}`}
        message={loadError}
        onRetry={() => fetchItems()}
      />
    );
  }

  const visibleSubcategories = allSubcategories.filter((c) =>
    activeChip === 'all' ? (grouped[c]?.length ?? 0) > 0 : c === activeChip
  );

  return (
    <>
      {/* ── Category chips ── */}
      <ShortlistChipStrip
        allCategories={allSubcategories} setAllCategories={setAllSubcategories}
        activeChip={activeChip} setActiveChip={setActiveChip}
        grouped={grouped} totalCount={items.length}
        addChipPlaceholder="Category name…"
        onRemoveCategory={requestRemoveCategory}
      />

      {/* ── Card grids ── */}
      <Box sx={{ px: PAGE_PX, pb: '20px', flex: 1 }}>
        {activeChip === 'all' && allSubcategories.every((c) => !(grouped[c]?.length)) && (
          <ShortlistEmptyState message="Save items from the Moodboard, or add your own above." />
        )}

        {visibleSubcategories.map((subcategory) => {
          const subcategoryItems = grouped[subcategory] || [];
          return (
            <Box key={subcategory} component="section" sx={{ mb: '20px' }}>
              <ShortlistSectionHeader label={subcategory} count={subcategoryItems.length} />
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: '8px',
              }}>
                {subcategoryItems.map((item, idx) => (
                  <ItemCard
                    key={item.id} item={item} idx={idx} arrLen={subcategoryItems.length}
                    category={subcategory}
                    onMoveUp={() => move(subcategory, idx, -1)}
                    onMoveDown={() => move(subcategory, idx, 1)}
                    onRemove={() => removeItem(subcategory, item)}
                    onUpdated={handleItemUpdated}
                    scenesData={scenesData}
                    onOpenSource={onOpenSource}
                    isGlowing={glowItemId === item.id}
                  />
                ))}
                <GhostCard onClick={() => setAddPhotoFor(subcategory)} />
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* ── Hint strip ── */}
      <ShortlistHintStrip message={
        <><Box component="span" sx={{ fontWeight: 500, color: CHARCOAL }}>01 is your top pick.</Box>
        {' '}Use the ↑ ↓ controls on each card to reorder within a category.
        </>
      } />

      {/* ── Remove category confirm box ── */}
      <RemoveConfirmDialog
        open={removeOpen}
        title={`Remove ${rm.name}?`}
        intro={`This also removes ${plural(rm.count, 'saved item')} from your ${tab.label.toLowerCase()} shortlist.`}
        notes={rm.found > 0
          ? [`${rm.found === rm.count ? (rm.count === 1 ? 'It' : 'All of them') : `${rm.found} of them`} came from your moodboard photos and will still be there, so you can save ${rm.found === 1 ? 'it' : 'them'} again.`]
          : []}
        confirmLabel="Remove category"
        busy={removeBusy}
        error={removeError}
        onCancel={() => setRemoveOpen(false)}
        onConfirm={confirmRemoveCategory}
      />

      {/* ── Add photo dialog ── */}
      <AddPhotoDialog
        open={!!addPhotoFor} categoryName={addPhotoFor}
        onClose={() => setAddPhotoFor(null)} onUploaded={handlePhotoUploaded}
        bucketName="furrrniture" filenameLabel={`${addPhotoFor}_${phoneNumber}`}
      />
    </>
  );
}

// ─── FurnitureShortlist (root) ────────────────────────────────────────────────

export default function FurnitureShortlist() {
  const [activeTab,  setActiveTab]  = useState(0);
  const [scenesData, setScenesData] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const phoneNumber = getTokenSubject();

  // "See in photo" pop-up. After it closes with changes, the open tab reloads.
  const bumpRefresh = useCallback(() => setRefreshKey((k) => k + 1), []);
  const sourcePeek  = useSourcePeek(bumpRefresh);

  // Photos are only used for the "came from this photo" link, so a failure
  // here isn't worth an error message: the link simply doesn't show.
  useEffect(() => {
    apiFetch('/api/scenes/my').then((d) => setScenesData(d || [])).catch(() => {});
  }, []);

  const tab = TABS[activeTab];

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column',
      height: '100%', overflow: 'hidden', ...appGridBg,
    }}>

      {/* ── Page header ── */}
      <PageHeader
        title="Rank your"
        accent="furnishings."
        description="Add furniture, fittings and appliances you're considering, or save them from your moodboard. Rank them as you go, so the final choice feels easy, not overwhelming."
      >
        <ScrollableTabStrip>
          <Box role="tablist" aria-label="Item types" sx={{ display: 'flex', alignItems: 'center' }}>
            {TABS.map((t, i) => (
              <Box
                key={t.id} component="button" type="button"
                role="tab" aria-selected={i === activeTab}
                onClick={() => setActiveTab(i)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  px: '14px', py: '9px', ...labelMono, fontSize: '9px', fontWeight: 700,
                  color: i === activeTab ? GREEN : CHARCOAL,
                  opacity: i === activeTab ? 1 : 0.6,
                  background: 'none', border: 'none',
                  borderBottom: `2px solid ${i === activeTab ? GREEN : 'transparent'}`,
                  cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                  transition: 'color 0.15s, border-color 0.15s, opacity 0.15s',
                  '&:focus-visible': { outline: `2px solid ${GREEN}`, outlineOffset: '-2px' },
                }}
              >
                {t.label}
              </Box>
            ))}
          </Box>
        </ScrollableTabStrip>
      </PageHeader>

      {/* ── Scrollable content — key forces a fresh start on tab switch ── */}
      <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        <TabContent
          key={tab.id} tab={tab} phoneNumber={phoneNumber}
          scenesData={scenesData}
          onOpenSource={sourcePeek.openPeek}
          glowItemId={sourcePeek.glowItemId}
          refreshKey={refreshKey}
        />
      </Box>

      <SourcePhotoPeek
        peek={sourcePeek.peek}
        shortlistName="Furniture"
        returnLabel="Fittings & Furnishings"
        onClose={sourcePeek.closePeek}
        onClosedWithChanges={sourcePeek.handleClosedWithChanges}
      />
    </Box>
  );
}
